import { Checkbox } from '@avanta/shared/components/ui/Checkbox';
import { DatePicker } from '@avanta/shared/components/ui/DatePicker';
import { Input } from '@avanta/shared/components/ui/Input';
import { Label } from '@avanta/shared/components/ui/Label';
import { Select } from '@avanta/shared/components/ui/Select';
import { Textarea } from '@avanta/shared/components/ui/Textarea';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { FIELD_VARIANT } from '@avanta/shared/constants/forms';
import { t } from '@avanta/shared/i18n';
import { formatDateISO, parseDate } from '@avanta/shared/utils/date';
import { useController } from 'react-hook-form';
import { RECORD_FIELD_TYPE } from 'constants/enterprise';
import { hasRecordFieldValue } from 'pages/AppointmentsReserve/utils';
import { type ContractRecordField, type RecordFieldType } from 'types/enterprises';
import {
  type RecordFieldName,
  type RecordFieldRules,
  type RecordFieldCommonProps,
  type RecordFieldInputProps,
  type RecordFieldRenderer,
  type RecordFieldsProps,
  type RecordFieldViewProps,
} from './types';

const getRecordFieldName = (recordField: ContractRecordField): RecordFieldName =>
  `recordFields.${recordField.key}`;

const getRecordFieldRules = (recordField: ContractRecordField): RecordFieldRules => ({
  required: recordField.required ? t('Common.required', { ns: LOCIZE_NS.COMMON }) : false,
  ...(recordField.maxLength ? { maxLength: recordField.maxLength } : {}),
});

const getErrorId = (recordField: ContractRecordField): string => `${recordField.key}-error`;

const getStringFieldValue = (value: RecordFieldViewProps['field']['value']): string =>
  typeof value === 'string' ? value : '';

const getRecordFieldPlaceholder = (
  recordField: ContractRecordField,
  hideEmptyPlaceholder: boolean
): string => {
  if (hideEmptyPlaceholder) return '';
  return recordField.placeholder ?? recordField.label;
};

const TextField = ({
  recordField,
  field,
  commonProps,
}: RecordFieldViewProps): React.ReactElement => (
  <Input
    {...commonProps}
    id={recordField.key}
    maxLength={recordField.maxLength ?? undefined}
    value={getStringFieldValue(field.value)}
    onChange={field.onChange}
    onBlur={field.onBlur}
    name={field.name}
    ref={field.ref}
  />
);

const TextareaField = ({
  recordField,
  field,
  commonProps,
}: RecordFieldViewProps): React.ReactElement => (
  <div className="col-span-2 flex w-full flex-col gap-2">
    <Label htmlFor={recordField.key} required={commonProps.required}>
      {commonProps.label}
    </Label>
    <Textarea
      id={recordField.key}
      name={field.name}
      ref={field.ref}
      maxLength={recordField.maxLength ?? undefined}
      value={getStringFieldValue(field.value)}
      onChange={field.onChange}
      onBlur={field.onBlur}
      readOnly={commonProps.readOnly}
      aria-describedby={commonProps.helperText ? getErrorId(recordField) : undefined}
      aria-invalid={commonProps.variant === FIELD_VARIANT.INVALID ? 'true' : undefined}
      className="min-h-20 w-full"
      placeholder={commonProps.placeholder}
    />
    {commonProps.helperText && (
      <span id={getErrorId(recordField)} className="text-xs text-red-500">
        {commonProps.helperText}
      </span>
    )}
  </div>
);

const DateField = ({ field, commonProps }: RecordFieldViewProps): React.ReactElement => {
  const dateValue =
    typeof field.value === 'string' && field.value
      ? parseDate(field.value, 'yyyy-MM-dd')
      : undefined;

  return (
    <DatePicker
      {...commonProps}
      value={dateValue}
      onChange={date => field.onChange(date ? formatDateISO(date) : undefined)}
    />
  );
};

const NumberField = ({
  recordField,
  field,
  commonProps,
}: RecordFieldViewProps): React.ReactElement => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    field.onChange(value === '' ? undefined : Number(value));
  };

  return (
    <Input
      {...commonProps}
      id={recordField.key}
      type="number"
      value={typeof field.value === 'number' ? field.value : ''}
      onChange={handleChange}
      onBlur={field.onBlur}
      name={field.name}
      ref={field.ref}
    />
  );
};

const BooleanField = ({
  recordField,
  field,
  commonProps,
}: RecordFieldViewProps): React.ReactElement => (
  <div className="flex flex-col gap-2">
    <Checkbox
      id={recordField.key}
      label={commonProps.label}
      required={commonProps.required}
      checked={field.value === true}
      onCheckedChange={checked => field.onChange(checked === true)}
      onBlur={field.onBlur}
      ref={field.ref}
      readOnly={commonProps.readOnly}
      aria-describedby={commonProps.helperText ? getErrorId(recordField) : undefined}
    />
    {commonProps.helperText && (
      <span id={getErrorId(recordField)} className="text-xs text-red-500">
        {commonProps.helperText}
      </span>
    )}
  </div>
);

const SelectField = ({
  recordField,
  field,
  commonProps,
}: RecordFieldViewProps): React.ReactElement => (
  <Select
    {...commonProps}
    options={recordField.options}
    value={getStringFieldValue(field.value)}
    onValueChange={field.onChange}
  />
);

const RECORD_FIELD_RENDERERS = {
  [RECORD_FIELD_TYPE.TEXT]: TextField,
  [RECORD_FIELD_TYPE.TEXTAREA]: TextareaField,
  [RECORD_FIELD_TYPE.DATE]: DateField,
  [RECORD_FIELD_TYPE.NUMBER]: NumberField,
  [RECORD_FIELD_TYPE.BOOLEAN]: BooleanField,
  [RECORD_FIELD_TYPE.SELECT]: SelectField,
} satisfies Record<RecordFieldType, RecordFieldRenderer>;

const RecordFieldInput = ({
  control,
  recordField,
  readOnly = false,
}: RecordFieldInputProps): React.ReactElement => {
  const { field, fieldState } = useController({
    control,
    name: getRecordFieldName(recordField),
    rules: getRecordFieldRules(recordField),
  });
  const Component = RECORD_FIELD_RENDERERS[recordField.type];
  const hideEmptyPlaceholder =
    readOnly && !recordField.required && !hasRecordFieldValue(field.value);
  const commonProps: RecordFieldCommonProps = {
    label: recordField.label,
    required: recordField.required,
    helperText: fieldState.error?.message,
    variant: fieldState.error ? FIELD_VARIANT.INVALID : FIELD_VARIANT.FILLED,
    placeholder: getRecordFieldPlaceholder(recordField, hideEmptyPlaceholder),
    readOnly,
  };

  return <Component recordField={recordField} field={field} commonProps={commonProps} />;
};

export const RecordFields = ({
  control,
  recordFields,
  readOnly = false,
  readOnlyInitialValues,
}: RecordFieldsProps): React.ReactElement => (
  <>
    {recordFields
      .filter(recordField => recordField.active)
      .sort(
        (firstRecordField, secondRecordField) => firstRecordField.order - secondRecordField.order
      )
      .map(recordField => (
        <RecordFieldInput
          key={recordField.id}
          control={control}
          recordField={recordField}
          readOnly={readOnly || hasRecordFieldValue(readOnlyInitialValues?.[recordField.key])}
        />
      ))}
  </>
);
